import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { DimensionInput } from "../DimensionInput";
import { UNITS } from "../DimensionInput/constants";

export interface SizingField {
  alwaysVisible: boolean;
  label: string;
  value: keyof SizingValues;
}

export interface SizingValues {
  height?: string;
  maxHeight?: string;
  maxWidth?: string;
  minHeight?: string;
  minWidth?: string;
  width?: string;
}

export interface SizingFormProps {
  styles?: {
    sizing?: SizingValues;
  };
}

const sizingFields: SizingField[] = [
  {
    value: "width",
    label: t("builder_ui.prop_type.styles.width"),
    alwaysVisible: true,
  },
  {
    value: "height",
    label: t("builder_ui.prop_type.styles.height"),
    alwaysVisible: true,
  },
  {
    value: "minWidth",
    label: t("builder_ui.prop_type.styles.min_width"),
    alwaysVisible: false,
  },
  {
    value: "minHeight",
    label: t("builder_ui.prop_type.styles.min_height"),
    alwaysVisible: false,
  },
  {
    value: "maxWidth",
    label: t("builder_ui.prop_type.styles.max_width"),
    alwaysVisible: false,
  },
  {
    value: "maxHeight",
    label: t("builder_ui.prop_type.styles.max_height"),
    alwaysVisible: false,
  },
];

const hasValue = (value: unknown): value is string =>
  value !== undefined &&
  value !== null &&
  value !== "" &&
  value !== "none" &&
  value !== "initial" &&
  value !== "auto";

export const Sizing = () => {
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();
  const { watch } = useFormContext<SizingFormProps>();
  const sizingValues = watch("styles.sizing");
  const hasInitialized = useRef(false);
  const [shownFields, setShownFields] = useState<string[]>([]);

  useEffect(() => {
    if (!hasInitialized.current && sizingValues) {
      const fieldsWithValues = sizingFields
        .filter(
          field => !field.alwaysVisible && hasValue(sizingValues[field.value]),
        )
        .map(field => field.value);

      if (fieldsWithValues.length > 0) {
        setShownFields(fieldsWithValues);
      }
      hasInitialized.current = true;
    }
  }, [sizingValues]);

  const handleAddField = useCallback((fieldValue: string) => {
    setShownFields(prev => [...prev, fieldValue]);
  }, []);

  const handleRemoveField = useCallback((fieldValue: string) => {
    setShownFields(prev => prev.filter(f => f !== fieldValue));
  }, []);

  const isFieldVisible = useCallback(
    (field: SizingField) =>
      field.alwaysVisible || shownFields.includes(field.value),
    [shownFields],
  );

  const hiddenFields = sizingFields.filter(
    field => !field.alwaysVisible && !shownFields.includes(field.value),
  );

  const dimensionOptions = useMemo(
    () =>
      hiddenFields.map(field => ({
        label: field.label,
        value: field.value,
        onClick: () => handleAddField(field.value),
      })),
    [hiddenFields, handleAddField],
  );

  if (isPropertiesPanelV2) {
    return (
      <div className="flex flex-col gap-3">
        {sizingFields.map(field => {
          const isVisible = isFieldVisible(field);
          return (
            <DimensionInput
              key={field.value}
              name={`styles.sizing.${field.value}`}
              label={field.label}
              options={field.alwaysVisible ? dimensionOptions : []}
              onRemove={
                !field.alwaysVisible
                  ? () => handleRemoveField(field.value)
                  : undefined
              }
              isHidden={!isVisible}
              {...((field.value === "maxWidth" ||
                field.value === "maxHeight") && {
                units: [...UNITS, "none"],
              })}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 grid-rows-3 gap-x-4 gap-y-2">
      {sizingFields.map(field => (
        <DimensionInput
          key={field.value}
          name={`styles.sizing.${field.value}`}
          label={field.label}
        />
      ))}
    </div>
  );
};
